/**
 * passkey-enroll-prompt — raises the enrolment offer. It renders nothing
 * itself; the surface it owns is the overlay, and the product decides when
 * to mount it: once per completed code sign-in, where the device holds no
 * passkey yet.
 *
 * On connect it waits RAISE_DELAY_MS, reads `passkeySupported$` once, and
 * opens `<passkey-enroll density="prompt">` with `show()` — sticky, so it is
 * announced as a dialog and a stray pointerdown does not take it away; not
 * draggable, since it is answered rather than moved aside; from a timer, so
 * with no gesture to anchor to it opens as the sheet. Escape, the backdrop,
 * "Not now" and "Done" close it. The product's `phase` is forwarded to the
 * open content, and the content's intents come back out on this element:
 * `passkey-enroll` while it is open, then exactly one of
 * `passkey-enroll-done` (the ceremony was read through to its payoff, or Done
 * pressed) or `passkey-enroll-defer` (every other way out) once it has
 * closed.
 */
import { SchmancyElement, schmancyElement } from '@mhmo91/schmancy/mixins';
import { show } from '@mhmo91/schmancy/overlay';
import { css, html, nothing, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { defaultIfEmpty, filter, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { passkeySupported$ } from '../support.ts';
import {
  type PasskeyEnroll,
  type PasskeyEnrollPhase,
  type PasskeyLanguage,
  passkeyEnrollAriaLabel,
} from './passkey-enroll.view.ts';
import './passkey-enroll.view.ts';

/** Held past the overlay's own ambient-anchor window, so the keystroke that
 *  submitted the last code digit is not taken for the gesture this blooms
 *  from, and the app's own routing lands first. */
export const RAISE_DELAY_MS = 1000;

@schmancyElement('passkey-enroll-prompt')
export class PasskeyEnrollPrompt extends SchmancyElement {
  static styles = [
    css`
      :host {
        display: contents;
      }
    `,
  ];

  @property({ type: String }) language: PasskeyLanguage = 'en';
  /** The address the passkey is bound to — the relying-party id. */
  @property({ type: String }) host = location.hostname;
  @property({ type: String }) phase: PasskeyEnrollPhase = 'idle';

  private readonly content = createRef<PasskeyEnroll>();

  private readonly close = (outcome: 'defer' | 'done') => () =>
    this.content.value?.dispatchEvent(
      new CustomEvent('close', { detail: outcome, bubbles: true, composed: true }),
    );

  override connectedCallback(): void {
    super.connectedCallback();
    timer(RAISE_DELAY_MS)
      .pipe(
        // Asked once, at the moment of the offer, and awaited rather than
        // sampled: the control has nothing to render without a platform
        // authenticator, so an unguarded raise opens an empty dialog.
        switchMap(() => passkeySupported$),
        take(1),
        filter(Boolean),
        switchMap(() =>
          show<'defer' | 'done'>(
            () => html`
              <passkey-enroll
                ${ref(this.content)}
                density="prompt"
                language=${this.language}
                host=${this.host}
                .phase=${this.phase}
                @passkey-enroll=${() =>
                  this.dispatchEvent(
                    new CustomEvent('passkey-enroll', { bubbles: true, composed: true }),
                  )}
                @passkey-enroll-defer=${this.close('defer')}
                @passkey-enroll-done=${this.close('done')}
              ></passkey-enroll>
            `,
            {
              sticky: true,
              drag: false,
              ariaLabel: passkeyEnrollAriaLabel(this.language, this.host),
            },
          ).pipe(defaultIfEmpty(undefined)),
        ),
        tap((outcome) =>
          this.dispatchEvent(
            new CustomEvent(outcome === 'done' ? 'passkey-enroll-done' : 'passkey-enroll-defer', {
              bubbles: true,
              composed: true,
            }),
          ),
        ),
        takeUntil(this.disconnecting),
      )
      .subscribe();
  }

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('phase') && this.content.value) this.content.value.phase = this.phase;
  }

  override render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'passkey-enroll-prompt': PasskeyEnrollPrompt;
  }
}
